/**
 * Shared UI primitives for the booking create/edit form and its 5 section
 * components. Previously defined inline in BookingCreateForm.jsx and
 * prop-drilled into each section — moved here so the sections can import
 * directly instead.
 */

export const Label = ({ children, required }) => (
  <label className="flex items-center gap-1 text-[13px] font-medium text-slate-700 dark:text-slate-200">
    {children}
    {required ? <span className="text-rose-600 dark:text-rose-400">*</span> : null}
  </label>
);

export const Help = ({ children }) =>
  children ? <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{children}</div> : null;

export const FieldError = ({ msg }) =>
  msg ? <div className="text-[12px] text-rose-600 dark:text-rose-400 mt-1">{msg}</div> : null;

export const TabButton = ({ active, onClick, children, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`booking-tab ${
      active
        ? "booking-tab-active"
        : disabled
        ? "opacity-50 cursor-not-allowed text-slate-400 dark:text-slate-500"
        : "booking-tab-inactive"
    }`}
  >
    {children}
  </button>
);

export const InputShell = ({ children, icon }) => (
  <div className="relative">
    {icon ? (
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">{icon}</span>
    ) : null}
    <div className={icon ? "pl-9" : ""}>{children}</div>
  </div>
);

const fieldCore =
  "w-full rounded-xl border px-3 py-2.5 text-sm transition focus:outline-none " +
  "bg-white dark:bg-slate-900 " +
  "text-slate-900 dark:text-slate-100 " +
  "placeholder-slate-400 dark:placeholder-slate-500 " +
  "focus:ring-4 ";
const fieldOk =
  "border-slate-200 dark:border-slate-600 " +
  "focus:ring-emerald-100 dark:focus:ring-emerald-900/40 " +
  "focus:border-emerald-500 dark:focus:border-emerald-500";
const fieldInvalid =
  "border-rose-400 dark:border-rose-500 " +
  "focus:ring-rose-100 dark:focus:ring-rose-900/40 " +
  "focus:border-rose-500 dark:focus:border-rose-500";

export const baseField = fieldCore + fieldOk;
export const fieldClass = (invalid) => fieldCore + (invalid ? fieldInvalid : fieldOk);

export const I = ({ invalid, ...p }) => <input className={`${fieldClass(invalid)} ${p.readOnly ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}`} aria-invalid={invalid || undefined} {...p} disabled={p.readOnly} />;
export const S = ({ invalid, ...p }) => <select className={`${fieldClass(invalid)} appearance-none ${p.readOnly ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}`} aria-invalid={invalid || undefined} {...p} disabled={p.readOnly} />;

/**
 * Every section card shares one brand accent (the same emerald → indigo →
 * amber gradient the form header uses) and one icon-bubble treatment,
 * instead of a different colour per card — that per-card rainbow read as
 * unreviewed/AI-generated. Callers pass a plain react-icons element; the
 * bubble supplies the colour.
 */
export function Card({ title, subtitle, children, icon, delay = 0 }) {
  return (
    <div className="booking-form-card" style={{ "--card-delay": `${delay}ms` }}>
      <div className="booking-form-card-header">
        {icon ? (
          <span className="booking-form-card-icon bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
            {icon}
          </span>
        ) : null}
        <div>
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</div>
          {subtitle ? <div className="text-[12px] text-slate-500 dark:text-slate-400">{subtitle}</div> : null}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function AirportPointRadio({ value, onChange, disabled = false }) {
  const opts = [
    { value: "AIRPORT", label: "Airport", icon: <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M21 16v-2l-8-5V3.5a1.5 1.5 0 1 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5L21 16z"/></svg> },
    { value: "POINT", label: "Any other Point", icon: <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"/></svg> },
  ];
  return (
    <div role="radiogroup" className="flex items-center gap-6">
      {opts.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            className={`group flex items-center gap-3 rounded-full border px-4 py-2 transition
              ${active
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-slate-700 border-slate-300 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-600"}
              disabled:opacity-60`}
          >
            <span className={`flex items-center justify-center w-7 h-7 rounded-full
              ${active ? "bg-white/15" : "bg-slate-50 dark:bg-slate-800"}`}>
              {opt.icon}
            </span>
            <span className={`text-sm ${active ? "font-medium" : ""}`}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* Pre-bound stagger-delay variants — one per form section, in visual order */
export const PassengerCard = (props) => <Card {...props} delay={160} />;
export const AssignmentCard = (props) => <Card {...props} delay={220} />;
export const PickupCard = (props) => <Card {...props} delay={280} />;
export const DropCard = (props) => <Card {...props} delay={340} />;
export const PricingCard = (props) => <Card {...props} delay={400} />;
